import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { User, Heart, Package } from 'lucide-react';
import ProfileSettingsTab from '@/components/settings/ProfileSettingsTab';
import KinksTab from '@/components/settings/KinksTab';
import ToysTab from '@/components/settings/ToysTab';

const TABS = [
    { key: 'profile', label: 'Profile', icon: User },
    { key: 'kinks', label: 'Kinks & Limits', icon: Heart },
    { key: 'toys', label: 'Toy Inventory', icon: Package },
] as const;

type TabKey = (typeof TABS)[number]['key'];

export default function SettingsPage() {
    const [searchParams, setSearchParams] = useSearchParams();
    const initialTab = (searchParams.get('tab') as TabKey) || 'profile';
    const [activeTab, setActiveTab] = useState<TabKey>(
        TABS.some((t) => t.key === initialTab) ? initialTab : 'profile',
    );

    const switchTab = (tab: TabKey) => {
        setActiveTab(tab);
        setSearchParams(tab === 'profile' ? {} : { tab });
    };

    return (
        <div className="space-y-6">
            <h1 className="text-xl font-semibold">Settings</h1>

            {/* Tab bar */}
            <div className="flex gap-1 rounded-lg bg-slate-800/50 p-1">
                {TABS.map((tab) => (
                    <button
                        key={tab.key}
                        type="button"
                        onClick={() => switchTab(tab.key)}
                        className={`flex flex-1 items-center justify-center gap-1.5 rounded-md px-3 py-2 text-sm font-medium transition-colors ${
                            activeTab === tab.key
                                ? 'bg-violet-900/50 text-violet-300'
                                : 'text-slate-500 hover:text-slate-300'
                        }`}
                    >
                        <tab.icon className="h-4 w-4" />
                        <span className="hidden sm:inline">{tab.label}</span>
                    </button>
                ))}
            </div>

            {/* Tab content */}
            {activeTab === 'profile' && <ProfileSettingsTab />}
            {activeTab === 'kinks' && <KinksTab />}
            {activeTab === 'toys' && <ToysTab />}
        </div>
    );
}
